import BigNumber from 'bignumber.js'

import { Array, pipe } from '@blade/std-lib'
import { Maker } from '@root/entities/trade'

import type { Trade } from '@root/entities/trade'

/**
 В формуле pnl = sellVolumeInUsd + remainingInToken * tokenPriceInUsd - buyVolumeInUsd

sellVolumeInUsd, remainingInToken, buyVolumeInUsd - надо обновлять из сокета свопов, когда видим что пришел своп холдера

tokenPriceInUsd - обновлять абсолютно после каждого свопа
 */

export const holdersSwapUpdateHandler = (
  holders: Array<Maker.RankedMaker>,
  newTxData: Trade.Trade,
): Array<Maker.RankedMaker> => {
  const balances = calcBalances(newTxData)
  const pnl = calcPnl(newTxData)
  const isNewTxDataNotOfHolder = holders.every(
    (holder) => holder.address !== newTxData.maker.address,
  )

  const updatedHoldersWithoutRanks: Array<Maker.Maker> =
    holders.map<Maker.Maker>((holder) => {
      if (holder.address !== newTxData.maker.address) {
        return {
          ...holder,
          ...balances(holder.balanceCurrency),
          pnl: pnl(holder),
        }
      }

      const result: Maker.Maker = {
        ...holder,
        buys: newTxData.maker.buys,
        buyVolumeCurrency: newTxData.maker.buyVolumeCurrency,
        buyVolumeInUsd: newTxData.maker.buyVolumeInUsd,
        sells: newTxData.maker.sells,
        sellVolumeCurrency: newTxData.maker.sellVolumeCurrency,
        sellVolumeInUsd: newTxData.maker.sellVolumeInUsd,
        pnl: newTxData.maker.pnl,
        lastTradeAt: newTxData.timestamp,
        balanceCurrency: newTxData.maker.balanceCurrency,
        txCount: newTxData.maker.sells + newTxData.maker.buys,
        ...balances(newTxData.maker.balanceCurrency),
      }

      return result
    })

  if (isNewTxDataNotOfHolder) {
    const newHolderWithoutRank: Maker.Maker = {
      ...newTxData.maker,
      balanceCurrency: newTxData.maker.balanceCurrency,
      ...balances(newTxData.maker.balanceCurrency),
      firstTradeAt: newTxData.timestamp,
      lastTradeAt: newTxData.timestamp,
      txCount: newTxData.maker.sells + newTxData.maker.buys,
      // TODO: Take from the swap
      balanceInNative: BigNumber(0),
      pnl30D: undefined,
      winRate30D: undefined,
    }
    updatedHoldersWithoutRanks.push(newHolderWithoutRank)
  }

  // recalculate ranks base on balanceInToken
  return pipe(
    updatedHoldersWithoutRanks,
    sortHoldersByBalanceInToken,
    Array.mapWithIndex(
      (holderWithoutRank, i): Maker.RankedMaker => ({
        ...holderWithoutRank,
        rank: i + 1,
      }),
    ),
    Array.take(holders.length),
  )
}

const sortHoldersByBalanceInToken = Array.sort<Maker.Maker>(
  Maker.cmpByBalanceInTokenDesc,
)

const calcBalances =
  (newTxData: Trade.Trade) => (balanceInToken: BigNumber) => {
    const balanceInUsd = balanceInToken.multipliedBy(newTxData.tokenPriceInUsd)
    return {
      balanceInUsd,
      balanceInUsdPercentage: balanceInUsd
        .dividedBy(newTxData.marketCapUsd)
        .multipliedBy(100),
    }
  }

// TODO: Use Pnl from finance module
const calcPnl = (newTxData: Trade.Trade) => (holder: Maker.Maker) => {
  const newBalanceInUsd = holder.balanceCurrency.multipliedBy(
    newTxData.tokenPriceInUsd,
  )
  return holder.sellVolumeInUsd
    .plus(newBalanceInUsd)
    .minus(holder.buyVolumeInUsd)
}
